import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const socket = io();

function App() {
  const [screen, setScreen] = useState('lobbyCreation');
  const [lobbyId, setLobbyId] = useState(null);
  const [username, setUsername] = useState('');
  const [joinLobbyId, setJoinLobbyId] = useState('');
   const [players, setPlayers] = useState({});
   const [scores, setScores] = useState({});
   const [teams, setTeams] = useState({});
   const [isOwner, setIsOwner] = useState(false);
  const [gameItems, setGameItems] = useState([]);
  const [guessedItems, setGuessedItems] = useState(new Set());
  const [inputText, setInputText] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentOronym, setCurrentOronym] = useState('');
     const [guessedBy, setGuessedBy] = useState({});
     const [winners, setWinners] = useState([]);
     const [showModal, setShowModal] = useState(false);
     const [modalData, setModalData] = useState(null);
     const [countdown, setCountdown] = useState(0);
     const [lastGuess, setLastGuess] = useState('');
      const [hasScrolled, setHasScrolled] = useState(false);
      const itemRefs = useRef([]);
      const gameItemsRef = useRef([]);
      const [seers, setSeers] = useState({});
      const displayedItems = gameItems.filter((_, index) => !guessedItems.has(index));

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lobby = urlParams.get('lobby');
    const usernameParam = urlParams.get('username');
    if (lobby) {
      setLobbyId(lobby);
    }
    if (usernameParam) {
      setUsername(usernameParam);
    }
    if (lobby && usernameParam) {
      socket.emit('join', { username: usernameParam, lobbyId: lobby });
      setScreen('lobby');
    } else if (lobby) {
      setScreen('usernameEntry');
    }

    socket.on('lobbyCreated', (id) => {
      setLobbyId(id);
      const url = new URL(window.location);
      url.searchParams.set('lobby', id);
      window.history.pushState({}, '', url);
      setScreen('usernameEntry');
    });

    socket.on('lobbyUpdate', ({ players, scores, teams, isOwner }) => {
      setPlayers(players);
      setScores(scores);
      setTeams(teams);
      setIsOwner(isOwner);
    });

    socket.on('gameStarted', ({ items, oronym, guessed = [], guessedBy = {}, seers = {}, countdown }) => {
      setGameItems(items);
      gameItemsRef.current = items;
      setSelectedItems(items);
      setCurrentOronym(oronym);
      setGuessedItems(new Set(guessed));
      setGuessedBy(guessedBy);
      setSeers(seers);
      if (countdown !== undefined) {
        setCountdown(countdown);
        setShowModal(true);
        // Set modal data to the last guessed item if available
        const lastGuessedIndex = Array.from(guessed).pop();
        if (lastGuessedIndex !== undefined && guessedBy[lastGuessedIndex]) {
          setModalData({ username: guessedBy[lastGuessedIndex].username, phrase: guessedBy[lastGuessedIndex].phrase, item: items[lastGuessedIndex], oronym });
        }
      }
      setScreen('game');
    });

    socket.on('buttonPressed', (user) => {
      console.log(`Player ${user} pressed button`);
    });

    socket.on('correctGuess', ({ username, phrase, index, oronym }) => {
      setGuessedItems(prev => new Set([...prev, index]));
      setGuessedBy(prev => ({ ...prev, [index]: { username, phrase } }));
      setModalData({ username, phrase, item: gameItemsRef.current[index], oronym });
      setShowModal(true);
    });

    socket.on('newOronym', ({ oronym, seers }) => {
      setCurrentOronym(oronym);
      setSeers(seers);
      setLastGuess('');
    });

     socket.on('scoreUpdate', (updatedScores) => {
       setScores(updatedScores);
     });

      socket.on('gameEnded', ({ winners }) => {
        setWinners(winners);
        setScreen('gameEnd');
      });

      socket.on('countdownUpdate', (seconds) => {
        setCountdown(seconds);
      });

      socket.on('countdownEnd', () => {
        setShowModal(false);
      });

      socket.on('seersUpdate', (updatedSeers) => {
        setSeers(updatedSeers);
      });

      return () => {
        socket.off('lobbyCreated');
        socket.off('lobbyUpdate');
        socket.off('gameStarted');
        socket.off('buttonPressed');
        socket.off('correctGuess');
        socket.off('newOonym');
        socket.off('scoreUpdate');
        socket.off('gameEnded');
        socket.off('countdownUpdate');
        socket.off('countdownEnd');
        socket.off('seersUpdate');
       };
    }, []);

  const correctPhrase = gameItems.find(item => item.oronym === currentOronym)?.Phrase || '';

  const isSeer = seers[teams[username]] === username;

  const createLobby = () => {
    socket.emit('createLobby');
  };

  const joinLobby = () => {
    if (joinLobbyId.trim()) {
      setLobbyId(joinLobbyId.trim());
      setScreen('usernameEntry');
    }
  };

  const joinGame = () => {
    if (username) {
      socket.emit('join', { username, lobbyId });
      const url = new URL(window.location);
      url.searchParams.set('username', username);
      window.history.pushState({}, '', url);
      setScreen('lobby');
    }
  };

  const selectTeam = (team) => {
    socket.emit('selectTeam', { team });
  };

  const startGame = () => {
    socket.emit('startGame');
  };

  const pressButton = () => {
    socket.emit('press');
  };

  const submitGuess = () => {
    if (!inputText.trim()) return;

    setLastGuess(inputText.trim());
    socket.emit('guess', { phrase: inputText.trim() });
    setInputText('');
  };

  const shareLobby = async () => {
    const url = window.location.origin + '/?lobby=' + lobbyId;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my Oronym game lobby',
          url: url
        });
      } catch (err) {
        console.log('Share failed', err);
      }
    } else {
      navigator.clipboard.writeText(url).then(() => {
        alert('Lobby URL copied to clipboard!');
      }).catch(err => {
        console.log('Copy failed', err);
      });
    }
   };

  const Scoreboard = ({ scores }) => {
    const sortedScores = Object.entries(scores)
      .map(([team, score]) => ({ team, score }))
      .sort((a, b) => b.score - a.score);

    return (
      <div className="bg-white p-4 rounded shadow mb-4">
        <h2 className="text-xl font-bold mb-2">Scoreboard</h2>
        <ul>
          {sortedScores.map(({ team, score }, index) => (
            <li key={index} className="flex justify-between">
              <span>{team} Team</span>
              <span>{score}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };



  return (
    <div>
      {screen === 'lobbyCreation' && (
        <div className="animate-fadeIn font-sans text-center py-12 px-12">
          <h1 className="text-4xl mb-4">Create or Join a Lobby</h1>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Enter lobby ID to join"
              value={joinLobbyId}
              onChange={(e) => setJoinLobbyId(e.target.value)}
              className="text-lg py-2 px-4 border border-gray-300 rounded mr-2"
            />
            <button onClick={joinLobby} className="text-xl py-2 px-4 bg-green-500 text-white rounded">Join Lobby</button>
          </div>
          <button onClick={createLobby} className="text-xl py-5 px-5 bg-blue-500 text-white rounded">Create Lobby</button>
        </div>
      )}
      {screen === 'usernameEntry' && (
        <div className="animate-fadeIn font-sans text-center py-12 px-12">
          <h1 className="text-4xl mb-4">Join the Game</h1>
          <input
            type="text"
            placeholder="Enter username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="text-lg py-2.5 px-2.5 mx-5 border border-gray-300 rounded"
          />
           <button onClick={joinGame} className="text-xl py-5 px-5 bg-green-500 text-white rounded">Join</button>
           {lobbyId && <p className="mt-4">Lobby: {lobbyId} <button onClick={shareLobby} className="ml-2 px-2 py-1 bg-blue-500 text-white rounded">Share</button></p>}
        </div>
      )}
       {screen === 'lobby' && (
         <div className="animate-fadeIn font-sans text-center py-12 px-12">
           <h1 className="text-4xl mb-4">Waiting in Lobby</h1>
           <p className="mb-4">Players online:</p>
            <ul className="mb-4">
              {Object.values(players).map((player, index) => (
                <li key={index} className="mb-2">{player} {teams[player] ? `(${teams[player]})` : '(No team)'}</li>
              ))}
            </ul>
           <div className="mb-4">
             <p className="mb-2">Choose your team:</p>
              <div className="flex justify-center space-x-4">
                {['Red', 'Blue', 'Green', 'Yellow', 'Orange'].map((team) => (
                 <button
                   key={team}
                   onClick={() => selectTeam(team)}
                   className={`px-4 py-2 rounded ${teams[username] === team ? 'bg-gray-500 text-white' : 'bg-white text-black border'}`}
                   disabled={teams[username] === team}
                 >
                   {team}
                 </button>
               ))}
             </div>
           </div>
           {isOwner && (
             <button onClick={startGame} className="text-xl py-5 px-5 bg-purple-500 text-white rounded">Start Game</button>
           )}
            {!isOwner && <p className="mb-4">Waiting for lobby owner to start the game...</p>}
            {lobbyId && <p>Lobby: {lobbyId} <button onClick={shareLobby} className="ml-2 px-2 py-1 bg-blue-500 text-white rounded">Share</button></p>}
         </div>
       )}
         {screen === 'game' && isSeer ? (
           <div className="fixed inset-0 bg-black text-white flex items-center justify-center text-6xl font-bold z-50">{currentOronym}</div>
         ) : screen === 'game' && (
              <div className="animate-fadeIn font-sans text-center py-12 px-12 flex flex-col h-screen">
               <Scoreboard scores={scores} />

               {!seers[teams[username]] && <p className="mb-4 text-xl">Guess the Oronym: <strong>{currentOronym}</strong></p>}

            <div className="mb-6">
             <input
               type="text"
               placeholder="Type the exact phrase..."
               value={inputText}
               onChange={(e) => setInputText(e.target.value)}
               onKeyPress={(e) => e.key === 'Enter' && submitGuess()}
               className="text-lg py-3 px-4 border border-gray-300 rounded mr-4 w-80"
             />
             <button
               onClick={submitGuess}
               className="text-lg py-3 px-6 bg-blue-500 text-white rounded hover:bg-blue-600"
             >
               Guess
             </button>
            </div>
            {lastGuess && (() => {
              return (
                <div className="mb-4">
                  <p className="text-sm text-gray-900">Last guess:</p>
                  <div className="flex font-mono text-lg">
                    {lastGuess.split('').map((char, i) => (
                      <span key={i} className={i < correctPhrase.length && char.toLowerCase() === correctPhrase[i].toLowerCase() ? 'text-green-500' : 'text-red-500'}>
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    ))}
                    {Array.from({length: Math.max(0, correctPhrase.length - lastGuess.length)}, (_, i) => (
                      <span key={lastGuess.length + i} className="text-red-500 ml-1">_</span>
                    ))}
                  </div>
                </div>
              );
            })()}
              <div className="overflow-y-auto flex-1 relative" onScroll={(e) => {
                const scrollPercentage = e.target.scrollTop / (e.target.scrollHeight - e.target.clientHeight);
                if (scrollPercentage > 0.05 && !hasScrolled) {
                  setHasScrolled(true);
                }
              }}>
                <div className="grid grid-cols-3 gap-1">
                  {displayedItems.map((item) => {
                    const originalIndex = gameItems.indexOf(item);
                    return (
                        <div
                          key={originalIndex}
                          ref={(el) => itemRefs.current[originalIndex] = el}
                          className="w-full rounded shadow bg-white overflow-hidden flex items-center justify-center"
                          style={{ aspectRatio: '1/1' }}
                        >
                          <img
                            src={`illustrations/${item.png_name}`}
                            alt={item.Phrase}
                            className="w-full h-full object-fill p-2"
                          />
                       </div>
                    );
                  })}
                </div>
                {!hasScrolled && (
                  <div className="sticky bottom-6 left-1/2 transform -translate-x-1/2 pointer-events-none opacity-75 animate-slow-pulse bg-white bg-opacity-80 rounded-full w-8 h-8 flex items-center justify-center">
                    <span className="text-gray-600 text-xl">↓</span>
                  </div>
                )}
              </div>
           </div>
         )}
        {screen === 'gameEnd' && (
          <div className="animate-fadeIn font-sans text-center py-12 px-12">
            <h1 className="text-4xl mb-4">Game Over!</h1>
            <h2 className="text-2xl mb-4">Winning Team{winners.length > 1 ? 's' : ''}:</h2>
            <ul className="mb-4">
              {winners.map((winner, index) => (
                <li key={index} className="text-xl mb-2">
                  {winner.team} Team with {winner.score} point{winner.score !== 1 ? 's' : ''}
                </li>
              ))}
            </ul>
             <Scoreboard scores={scores} />
           </div>
         )}

         {showModal && modalData && (
           <div className="fixed inset-0 bg-stone-500/50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full mx-4 animate-gentleBounce">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-green-600 mb-4">🎉 Correct Guess! 🎉</h2>
                 <img
                   src={`illustrations/${modalData.item.png_name}`}
                   alt={modalData.item.Phrase}
                   className="w-32 h-32 object-contain mx-auto mb-4"
                 />
                 <p className="text-lg mb-2">oronym: <span className="font-semibold">{modalData.oronym}</span></p>
                 <p className="text-lg mb-2">
                   <span className="font-bold text-purple-600">{modalData.username}</span> guessed:
                 </p>
                 <p className="text-xl font-semibold text-green-600 mb-4">{modalData.phrase}</p>
                 <p className="text-sm text-gray-500">Game resumes in {countdown} second{countdown !== 1 ? 's' : ''}...</p>
              </div>
            </div>
          </div>
        )}



      </div>
  );
}

export default App;
